import i18n from '@/i18n/i18n';
import {SupportedLanguage} from '@/types/common';
import {Company, CompanyStatistics} from '@/types/company';
import {
  Grid,
  IconButton,
  Paper,
  SxProps,
  Typography,
  useTheme
} from '@mui/material';
import {useCallback, useState} from 'react';
import Linkify from 'linkify-react';
import CompanyStatisticsComponent from './company-statistics';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from '@/contexts/snackbar';
import EditIcon from '@mui/icons-material/Edit';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import CompanyInfosEditForm from '@/components/forms/company/update';

const prepareInitialData = (data: Company) => {
  const defaultImprint = {
    labels: {
      de: '',
      en: ''
    },
    url: ''
  };

  const imprintValues = data?.imprint
    ? {
        labels: {
          de: data.imprint.labels?.de || defaultImprint.labels.de,
          en: data.imprint.labels?.en || defaultImprint.labels.en
        },
        url: data.imprint.url || defaultImprint.url
      }
    : defaultImprint;

  return {
    ...data,
    title: data?.title || '',
    email: data?.email || '',
    welcome: data?.welcome || '',
    labelLanguage: data?.labelLanguage || 'de',
    imprint: imprintValues
  };
};

interface CompanyInfosPropsForManager {
  companyData: Company;
  companyStatistics?: CompanyStatistics | null;
  sx?: SxProps;
  statisticsSx?: SxProps;
  formAdminMode?: boolean;
}

const CompanyInfosForManager: React.FC<CompanyInfosPropsForManager> = ({
  companyData,
  companyStatistics,
  sx,
  statisticsSx,
  formAdminMode
}) => {
  const theme = useTheme();

  const {t} = useTranslation();

  const {showSnackbarMessage} = useSnackbar();

  const currentLanguage = i18n.language as SupportedLanguage;

  const [showCompanyInfosForm, setShowCompanyInfosForm] = useState(false);

  const getFullUrl = useCallback((url: string) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    } else if (url.startsWith('www.')) {
      return 'http://' + url;
    } else {
      return url;
    }
  }, []);

  const handleEditButtonClick = useCallback(() => {
    setShowCompanyInfosForm(true);
  }, []);

  const onCompanyInfosUpdated = useCallback(() => {
    setShowCompanyInfosForm(false);
    showSnackbarMessage(t('company_update_success'), 'success');
  }, [showSnackbarMessage, t]);

  const onCompanyInfosUpdateError = useCallback(() => {
    showSnackbarMessage(t('company_update_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleCompanyInfosUpdateCancel = useCallback(() => {
    setShowCompanyInfosForm(false);
  }, []);

  return (
    <>
      <Paper elevation={3} sx={{p: 3}}>
        <Grid container spacing={3} sx={sx}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {companyData.title}
            </Typography>
          </Grid>

          {companyData.welcome && (
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                <Linkify
                  options={{
                    target: '_blank',
                    rel: 'noopener noreferrer',
                    attributes: {
                      style: {
                        color: theme.palette.customColors?.urlLink
                      }
                    }
                  }}
                >
                  {companyData.welcome}
                </Linkify>
              </Typography>
            </Grid>
          )}

          {companyStatistics && (
            <CompanyStatisticsComponent
              companyStatistics={companyStatistics}
              sx={statisticsSx}
            />
          )}
          {companyData.email && (
            <Grid item xs={12}>
              <Typography variant="body1" gutterBottom>
                {companyData.email}
              </Typography>
            </Grid>
          )}

          {companyData.imprint?.url &&
            companyData.imprint?.labels?.[currentLanguage] && (
              <Grid item xs={12}>
                <Typography variant="body1" gutterBottom>
                  <a
                    href={getFullUrl(companyData.imprint.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: theme.palette.customColors?.urlLink,
                      textDecoration: 'underline'
                    }}
                  >
                    {companyData.imprint.labels[currentLanguage]}
                  </a>
                </Typography>
              </Grid>
            )}
        </Grid>
      </Paper>

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        display={'flex'}
        spacing={2}
        marginTop={3}
      >
        <Grid
          item
          xs={12}
          sm={6}
          justifyContent="center"
          alignItems="center"
          display={'flex'}
        >
          <IconButton
            color="secondary"
            aria-label={t('company_infos_edit')}
            onClick={handleEditButtonClick}
            sx={{
              borderRadius: '4px',
              overflow: 'hidden',
              fontSize: {
                xs: '14px',
                sm: '18px'
              }
            }}
          >
            <EditIcon
              sx={{
                marginRight: 1
              }}
            />
            {t('company_infos_edit')}
          </IconButton>
        </Grid>
        <Grid
          item
          xs={12}
          justifyContent="center"
          alignItems="center"
          display={'flex'}
        ></Grid>
      </Grid>

      <DialogForm
        open={showCompanyInfosForm}
        onSubmit={onCompanyInfosUpdated}
        onCancel={handleCompanyInfosUpdateCancel}
        onError={onCompanyInfosUpdateError}
        FormComponent={CompanyInfosEditForm}
        maxHeightAllowed={'1200px'}
        initialValues={formAdminMode && prepareInitialData(companyData)}
        updatingAdminCompany={formAdminMode}
        id={companyData._id}
      />
    </>
  );
};

export default CompanyInfosForManager;
